import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import {
	ArrowLeft,
	BadgePercent,
	CalendarClock,
	Copy,
	History,
	Pencil,
	Power,
	Target,
	UserPlus,
} from "lucide-react"
import { useCallback, useState } from "react"
import { CouponStatusBadge } from "@/components/coupons/coupon-status-badge"
import { CurrencyAmountsTable } from "@/components/coupons/currency-amounts-table"
import { DataTable } from "@/components/data-table/data-table"
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header"
import type { DataTableColumnDef } from "@/components/data-table/data-table-features"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
} from "@/components/layout"
import { BACK_LINK_CLASS } from "@/components/promotions/back-link"
import {
	DetailColumn,
	DetailLayout,
	DetailPage,
	DetailPageHeader,
	PanelTable,
} from "@/components/promotions/detail-page"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { AddEligibleUsersDialog } from "@/features/coupons-admin/AddEligibleUsersDialog"
import { CouponEligibleUsersTab } from "@/features/coupons-admin/CouponEligibleUsersTab"
import { createTabSearch } from "@/hooks/use-url-tab"
import {
	useAdminCoupon,
	useCouponEligibleUsers,
	useCouponRedemptions,
	useDeactivateCoupon,
} from "@/queries/marketplace"
import type {
	AdminCoupon,
	CouponRedemptionDto,
} from "@/types/marketplace.types"

const TABS = ["overview", "redemptions", "eligible"] as const
type CouponTab = (typeof TABS)[number]

export interface CouponDetailSearch {
	tab?: CouponTab
	/** Opens the "Add users" dialog on load (set right after creating a restricted coupon). */
	addUsers?: true
}

export const Route = createFileRoute("/coupons/$couponId/")({
	component: CouponDetailPage,
	validateSearch: (search: Record<string, unknown>): CouponDetailSearch => ({
		...createTabSearch(TABS)(search),
		...(search.addUsers === true || search.addUsers === "true"
			? { addUsers: true as const }
			: {}),
	}),
})

function formatCurrency(cents: number, currency: string): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100)
}

function getRedemptionColumns(
	currency: string,
): DataTableColumnDef<CouponRedemptionDto>[] {
	return [
		{
			accessorKey: "username",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="User" />
			),
			cell: ({ row }) => (
				<Link
					to="/users/$userId"
					params={{ userId: row.original.userId }}
					className="flex items-center gap-2 hover:text-primary-text"
				>
					<Avatar className="size-5">
						<AvatarImage src={row.original.userImageUrl ?? undefined} />
						<AvatarFallback className="text-[9px]">
							{row.original.username.slice(0, 2).toUpperCase()}
						</AvatarFallback>
					</Avatar>
					<span className="max-w-[160px] truncate">
						@{row.original.username}
					</span>
				</Link>
			),
		},
		{
			accessorKey: "transactionNumber",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Transaction" />
			),
			cell: ({ row }) => (
				<span className="font-mono">
					{row.original.transactionNumber ??
						row.original.transactionId.slice(0, 12)}
				</span>
			),
		},
		{
			accessorKey: "discountAmount",
			header: ({ column }) => (
				<DataTableColumnHeader
					column={column}
					title="Discount"
					className="justify-end"
				/>
			),
			cell: ({ row }) => (
				<span className="block text-right tabular-nums">
					{formatCurrency(row.original.discountAmount, currency)}
				</span>
			),
		},
		{
			accessorKey: "redeemedAt",
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Redeemed at" />
			),
			cell: ({ row }) => (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="cursor-default text-muted-foreground">
							{formatDistanceToNow(new Date(row.original.redeemedAt), {
								addSuffix: true,
							})}
						</span>
					</TooltipTrigger>
					<TooltipContent>
						<p>{new Date(row.original.redeemedAt).toLocaleString()}</p>
					</TooltipContent>
				</Tooltip>
			),
		},
	]
}

function regionLabel(region: string | null | undefined): string {
	if (!region) return "All regions"
	return region === "NA" ? "North America" : "Europe"
}

function formatCouponValue(coupon: AdminCoupon): string {
	if (coupon.type === "percentage") return `${coupon.value}%`
	if (coupon.currencyAmounts && coupon.currencyAmounts.length > 1) {
		return `${coupon.currencyAmounts.length} currencies`
	}
	return formatCurrency(coupon.value, coupon.currency ?? "USD")
}

function CouponOverviewTab({ coupon }: { coupon: AdminCoupon }) {
	const hasCurrencyAmounts =
		coupon.currencyAmounts && coupon.currencyAmounts.length > 0

	return (
		<DetailColumn>
			<DetailPanel title="Discount" icon={BadgePercent}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Type">
							{coupon.type === "percentage" ? "Percentage" : "Fixed amount"}
						</KeyValueItem>
						{coupon.type === "percentage" && (
							<KeyValueItem label="Percentage">{coupon.value}%</KeyValueItem>
						)}
						{!hasCurrencyAmounts && coupon.type === "fixed_amount" && (
							<KeyValueItem label="Value">
								{formatCurrency(coupon.value, coupon.currency ?? "USD")}
							</KeyValueItem>
						)}
						{!hasCurrencyAmounts && coupon.currency && (
							<KeyValueItem label="Currency">{coupon.currency}</KeyValueItem>
						)}
						{!hasCurrencyAmounts && coupon.maximumDiscountAmount !== null && (
							<KeyValueItem label="Maximum discount">
								{formatCurrency(
									coupon.maximumDiscountAmount,
									coupon.currency ?? "USD",
								)}
							</KeyValueItem>
						)}
						{!hasCurrencyAmounts && coupon.minimumOrderAmount !== null && (
							<KeyValueItem label="Minimum order amount">
								{formatCurrency(
									coupon.minimumOrderAmount,
									coupon.currency ?? "USD",
								)}
							</KeyValueItem>
						)}
					</KeyValueList>
				</DetailPanelSection>

				{hasCurrencyAmounts && (
					<DetailPanelSection
						label={
							coupon.type === "fixed_amount"
								? "Per-currency amounts"
								: "Per-currency settings"
						}
					>
						<PanelTable>
							<CurrencyAmountsTable
								rows={coupon.currencyAmounts}
								showAmount={coupon.type === "fixed_amount"}
								bare
							/>
						</PanelTable>
					</DetailPanelSection>
				)}
			</DetailPanel>

			<DetailPanel title="Targeting and restrictions" icon={Target}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Audience">
							{coupon.audience === "restricted" ? (
								<>
									Restricted
									{coupon.eligibleUserCount != null && (
										<span className="text-meta">
											{` · ${coupon.eligibleUserCount.toLocaleString()} user${coupon.eligibleUserCount === 1 ? "" : "s"}`}
										</span>
									)}
								</>
							) : (
								"Everyone"
							)}
						</KeyValueItem>
						{coupon.program && (
							<KeyValueItem label="Program">
								<Link
									to="/coupons/programs/$programId"
									params={{ programId: coupon.program.id }}
									className="text-primary-text hover:underline"
								>
									{coupon.program.name}
								</Link>
							</KeyValueItem>
						)}
						{coupon.issuedToUserId && (
							<KeyValueItem label="Issued to">
								<Link
									to="/users/$userId"
									params={{ userId: coupon.issuedToUserId }}
									className="font-mono text-foreground hover:text-primary-text"
								>
									{coupon.issuedToUserId}
								</Link>
							</KeyValueItem>
						)}
						<KeyValueItem label="Region">
							{regionLabel(coupon.region)}
						</KeyValueItem>
						{coupon.applicableCurrencies.length > 0 && (
							<KeyValueItem label="Currencies">
								{coupon.applicableCurrencies.join(", ")}
							</KeyValueItem>
						)}
						<KeyValueItem label="New buyers only">
							{coupon.newBuyersOnly ? "Yes" : "No"}
						</KeyValueItem>
						<KeyValueItem label="Account must predate coupon">
							{coupon.accountMustPredateCoupon ? "Yes" : "No"}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
		</DetailColumn>
	)
}

function CouponSummary({ coupon }: { coupon: AdminCoupon }) {
	return (
		<DetailColumn>
			<DetailPanel title="Usage and schedule" icon={CalendarClock}>
				<DetailPanelSection label="Usage limits">
					<KeyValueList>
						<KeyValueItem label="Total redemptions">
							{coupon.redemptionCount}
							{coupon.maxRedemptions !== null ? (
								<span className="text-meta"> / {coupon.maxRedemptions}</span>
							) : (
								<span className="text-meta"> (unlimited)</span>
							)}
						</KeyValueItem>
						<KeyValueItem label="Per user limit">
							{coupon.maxRedemptionsPerUser}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
				<DetailPanelSection label="Schedule">
					<KeyValueList>
						<KeyValueItem label="Starts at">
							{format(new Date(coupon.startsAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Expires at">
							{coupon.expiresAt ? (
								format(new Date(coupon.expiresAt), "PPP p")
							) : (
								<span className="text-meta">No expiry</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
			<DetailPanel title="Record" icon={History}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Created">
							{format(new Date(coupon.createdAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Last updated">
							{format(new Date(coupon.updatedAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Created by">
							{coupon.createdByUsername ? (
								<span>@{coupon.createdByUsername}</span>
							) : coupon.program ? (
								<span>Program · {coupon.program.name}</span>
							) : (
								<span className="font-mono">{coupon.createdBy ?? "None"}</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
		</DetailColumn>
	)
}

function CouponRedemptionsTab({
	couponId,
	currency,
}: {
	couponId: string
	currency: string
}) {
	const [page, setPage] = useState(1)
	const { redemptions, isLoading, total, totalPages } = useCouponRedemptions({
		couponId,
		page,
		limit: 20,
	})

	return (
		<DataTable
			columns={getRedemptionColumns(currency)}
			data={redemptions}
			tableId="coupon-redemptions"
			manualPagination
			pageCount={totalPages}
			pageIndex={page - 1}
			pageSize={20}
			totalCount={total}
			onPaginationChange={(newPageIndex) => setPage(newPageIndex + 1)}
			isLoading={isLoading}
		/>
	)
}

function CouponDetailPage() {
	const { couponId } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { coupon, isLoading, error } = useAdminCoupon({ couponId })
	const deactivateMutation = useDeactivateCoupon()
	const [showDeactivateDialog, setShowDeactivateDialog] = useState(false)
	const [addUsersOpen, setAddUsersOpen] = useState(!!searchParams.addUsers)
	const [eligibleRefreshToken, setEligibleRefreshToken] = useState(0)

	const isRestricted = coupon?.audience === "restricted"
	const { total: eligibleTotal } = useCouponEligibleUsers({
		couponId,
		page: 1,
		limit: 1,
		enabled: isRestricted,
	})

	const requestedTab = searchParams.tab ?? "overview"
	const activeTab =
		requestedTab === "eligible" && !isRestricted ? "overview" : requestedTab
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/coupons/$couponId",
			params: { couponId },
			search: tab === "overview" ? {} : { tab: tab as CouponTab },
			replace: true,
		})
	}

	const handleAddUsersOpenChange = useCallback(
		(open: boolean) => {
			setAddUsersOpen(open)
			if (!open && searchParams.addUsers) {
				navigate({
					to: "/coupons/$couponId",
					params: { couponId },
					search: { tab: "eligible" },
					replace: true,
				})
			}
		},
		[couponId, navigate, searchParams.addUsers],
	)

	const handleDeactivate = useCallback(async () => {
		if (!coupon) return
		await deactivateMutation.mutateAsync(coupon.id)
		setShowDeactivateDialog(false)
	}, [coupon, deactivateMutation])

	if (isLoading) {
		return (
			<DetailPage>
				<Skeleton className="h-8 w-64" />
				<DetailLayout>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9 w-full max-w-80" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
					<Skeleton className="h-80" />
				</DetailLayout>
			</DetailPage>
		)
	}

	if (error || !coupon) {
		return (
			<DetailPage className="items-start gap-3">
				<p className="text-bad">
					{error?.message || "The coupon could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/coupons" search={{} as never}>
						<ArrowLeft />
						Back to coupons
					</Link>
				</Button>
			</DetailPage>
		)
	}

	const facts = (
		<span className="flex flex-wrap items-center gap-x-2 gap-y-1 tabular-nums">
			<span>
				{coupon.type === "percentage" ? "Percentage" : "Fixed amount"}
			</span>
			<span className="text-meta">·</span>
			<span className="text-foreground">{formatCouponValue(coupon)}</span>
			<span className="text-meta">·</span>
			<span>
				<span className="text-foreground">{coupon.redemptionCount}</span>
				{coupon.maxRedemptions !== null
					? ` of ${coupon.maxRedemptions} redemptions`
					: " redemptions"}
			</span>
			<span className="text-meta">·</span>
			<span>
				Created{" "}
				{formatDistanceToNow(new Date(coupon.createdAt), { addSuffix: true })}
			</span>
		</span>
	)

	return (
		<DetailPage>
			<DetailPageHeader
				back={
					<Link to="/coupons" search={{} as never} className={BACK_LINK_CLASS}>
						<ArrowLeft />
						Coupons
					</Link>
				}
				title={<span className="font-mono break-all">{coupon.code}</span>}
				badges={<CouponStatusBadge coupon={coupon} />}
				description={
					<span className="flex flex-col gap-1">
						{coupon.description && <span>{coupon.description}</span>}
						{facts}
					</span>
				}
				actions={
					<>
						{isRestricted && (
							<Button onClick={() => setAddUsersOpen(true)}>
								<UserPlus />
								Add users
							</Button>
						)}
						<Button variant="outline" asChild>
							<Link to="/coupons/new" search={{ from: coupon.id }}>
								<Copy />
								Duplicate
							</Link>
						</Button>
						<Button variant="outline" asChild>
							<Link
								to="/coupons/$couponId/edit"
								params={{ couponId: coupon.id }}
							>
								<Pencil />
								Edit
							</Link>
						</Button>
						{coupon.isActive && (
							<Button
								variant="destructiveOutline"
								onClick={() => setShowDeactivateDialog(true)}
							>
								<Power />
								Deactivate
							</Button>
						)}
					</>
				}
			/>

			<DetailLayout>
				<Tabs
					value={activeTab}
					onValueChange={setActiveTab}
					className="min-w-0 gap-6"
				>
					<TabsList variant="line">
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="redemptions" className="gap-2">
							Redemptions
							{coupon.redemptionCount > 0 && (
								<span className="text-meta tabular-nums">
									{coupon.redemptionCount}
								</span>
							)}
						</TabsTrigger>
						{isRestricted && (
							<TabsTrigger value="eligible" className="gap-2">
								Eligible users
								<span className="text-meta tabular-nums">
									{eligibleTotal.toLocaleString()}
								</span>
							</TabsTrigger>
						)}
					</TabsList>

					<TabsContent value="overview" className="mt-0">
						<CouponOverviewTab coupon={coupon} />
					</TabsContent>

					<TabsContent value="redemptions" className="mt-0">
						<CouponRedemptionsTab
							couponId={coupon.id}
							currency={coupon.currency ?? "USD"}
						/>
					</TabsContent>

					{isRestricted && (
						<TabsContent value="eligible" className="mt-0">
							<CouponEligibleUsersTab
								couponId={coupon.id}
								total={eligibleTotal}
								refreshToken={eligibleRefreshToken}
								onRefresh={() => setEligibleRefreshToken((n) => n + 1)}
								onAddUsers={() => setAddUsersOpen(true)}
							/>
						</TabsContent>
					)}
				</Tabs>

				<CouponSummary coupon={coupon} />
			</DetailLayout>

			{isRestricted && (
				<AddEligibleUsersDialog
					couponId={coupon.id}
					couponCode={coupon.code}
					open={addUsersOpen}
					onOpenChange={handleAddUsersOpenChange}
					onCompleted={() => setEligibleRefreshToken((n) => n + 1)}
				/>
			)}

			{/* Deactivate Dialog */}
			<AlertDialog
				open={showDeactivateDialog}
				onOpenChange={setShowDeactivateDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Deactivate Coupon</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to deactivate{" "}
							<span className="font-mono font-semibold">{coupon.code}</span>?
							This will prevent it from being used in any future checkouts. This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeactivate}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Deactivate
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</DetailPage>
	)
}
